import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AuthType } from '@core/enums/auth.enum';
import { Translations } from '@core/models/translations.model';
import { filter, finalize, tap } from 'rxjs';
import { AuthFacade } from '../../auth.facade';
import { OtpComponent } from '../otp/otp.component';
import { PhoneValidator } from '@core/validators';
import { SnackService } from '@shared/services/snack.service';
import { CookieService } from 'ngx-cookie-service';

const loginText = {
	headerTitle: 'n535',
	headerDescription: 'n536',
};

const updateMobileText = {
	headerTitle: 'n548',
	headerDescription: 'n549',
};

@Component({
	selector: 'app-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
	constructor(private readonly fb: FormBuilder, private authFacade: AuthFacade, private snackService: SnackService, private cookie: CookieService) {}

	@Input() authType = AuthType.LOGIN;
	@Input() translations: Translations;

	@Output() changed = new EventEmitter();

	form!: FormGroup;
	text = loginText;
	ngxMaskPhoneInput = {
		prefix: '',
		mask: '',
	};
	isLoading = false;

	onSendVerification(): void {
		this.checkNumber();
	}

	/**
	 * To check if number is valid from backend api call and log its entry
	 */
	checkNumber(): void {
		this.isLoading = true;
		const phoneWithoutCountryCode = this.authFacade.getTestPhoneNumber(this.form.get('phone').value);
		const phonePrefix = this.authFacade.getTestPhonePrefix(phoneWithoutCountryCode);

		this.authFacade
			.checkNumberIsValid({
				phone: phoneWithoutCountryCode,
				phonePrefix: phonePrefix,
			})
			.pipe(finalize(() => (this.isLoading = false)))
			.subscribe((res) => {
				if (res.status === '1') {
					//to check if same number is retried within 60 secs
					const storedPhoneNumber = this.cookie.get('phoneNumber');
					if (phoneWithoutCountryCode === storedPhoneNumber && this.authType === AuthType.LOGIN) {
						const startTimeStamp = parseInt(this.cookie.get('startTimeStamp'), 10);
						const phoneWithCountryCode = `${phonePrefix}${phoneWithoutCountryCode.substring(1)}`;
						this.gotoOtp(phoneWithoutCountryCode, phonePrefix, phoneWithCountryCode, startTimeStamp);
					} else {
						this.handleSendVerification();
					}
				} else {
					this.snackService.open(res.message, 5000, null, 'error'); // remove this after TWILIO is back
				}
			});
	}

	/**
	 * Only activate the masking on phone input focus
	 */
	onPhoneInputFocus(): void {
		this.ngxMaskPhoneInput = {
			prefix: '07',
			mask: '0*',
		};
	}

	handlePaste(event: ClipboardEvent): void {
		const clipboardData = event.clipboardData || window['clipboardData'];
		const pastedText = clipboardData.getData('text');

		// Remove any non-digit characters from the pasted text
		const cleanedText = pastedText.replace(/\D/g, '');

		if (cleanedText.startsWith('07')) {
			// Remove the leading '07' from the pasted text
			const NEW_VALUE = cleanedText.substring(2, 11); // Keep only the first 9 digits
			this.form.get('phone').setValue(NEW_VALUE);
		} else {
			const NEW_VALUE = cleanedText.substring(0, 9); // Keep only the first 9 digits
			this.form.get('phone').setValue(NEW_VALUE);
		}
	}

	/**
	 * Update new phone number
	 */
	private verifyNewMobile(): void {
		const phoneWithoutCountryCode = this.authFacade.getTestPhoneNumber(this.form.get('phone').value);
		const phonePrefix = this.authFacade.getTestPhonePrefix(phoneWithoutCountryCode);

		this.authFacade
			.checkNumberExist({
				phone: phoneWithoutCountryCode,
				phonePrefix,
			})
			.pipe(
				tap(async (isRegistered: boolean) => {
					if (isRegistered) {
						this.form.get('phone').setErrors({ phoneRegistered: true });
					}
				}),
				filter((isRegistered: boolean) => !isRegistered),
				finalize(() => (this.isLoading = false))
			)
			.subscribe(async () => await this.verifyMobile());
	}

	/**
	 * Login/Register new number
	 */
	private async verifyMobile(): Promise<void> {
		const phoneWithoutCountryCode = this.authFacade.getTestPhoneNumber(this.form.get('phone').value);
		const phonePrefix = this.authFacade.getTestPhonePrefix(phoneWithoutCountryCode);
		const phoneWithCountryCode = `${phonePrefix}${phoneWithoutCountryCode.substring(1)}`;

		/**
		 * Firebase phone authentication first then fallback error to Twilio authentication.
		 */

		try {
			//Previously FIREBASE CODE  was used to send first OTP
			// await this.authFacade.signInWithPhoneNumber(phoneWithCountryCode);

			// now Using TWILIO CODE
			await this.authFacade.signInWithPhoneNumberTwilio({
				phone: phoneWithoutCountryCode,
				phonePrefix: phonePrefix,
			});
			this.gotoOtp(phoneWithoutCountryCode, phonePrefix, phoneWithCountryCode);
		} catch (error) {
			// TWILIO CODE
			// await this.authFacade
			// 	.signInWithPhoneNumberTwilio({
			// 		phone: phoneWithoutCountryCode,
			// 		phonePrefix: phonePrefix,
			// 	})
			// 	.then(() => this.gotoOtp(phoneWithoutCountryCode, phonePrefix, phoneWithCountryCode))
			// 	.catch(() => this.snackService.open(this.translations['n174'], 5000, null, 'error'))
			// 	.finally(() => (this.isLoading = false));
			this.snackService.open(this.translations['n270'], 5000, null, 'error'); // remove this after TWILIO is back
		} finally {
			this.isLoading = false;
		}
	}

	/**
	 *  Go to OTP screen
	 */
	private gotoOtp(phone: string, phonePrefix: string, phoneWithCountryCode: string, startTimeStamp?: number): void {
		this.changed.emit({
			component: OtpComponent,
			phone,
			phonePrefix,
			phoneWithCountryCode,
			authType: this.authType,
			timeStart: startTimeStamp,
		});
	}

	private handleSendVerification() {
		this.isLoading = true;

		switch (this.authType) {
			case AuthType.LOGIN:
			case AuthType.VERIFY_OLD_MOBILE:
				this.verifyMobile();

				break;
			case AuthType.VERIFY_NEW_MOBILE:
				this.verifyNewMobile();

				break;
			default:
		}
	}

	private createForm(): void {
		this.form = this.fb.group({
			phone: ['', [PhoneValidator.validatePhone()]],
		});
	}

	private setProperties(): void {
		if (this.authType !== AuthType.LOGIN) {
			this.text = updateMobileText;
		}
	}

	ngOnInit(): void {
		this.createForm();
		this.setProperties();
	}
}
